<template>
	<div class="container_section p-5">
		<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
			<div class="is-flex is-align-items-center">
				<Ecs class="mr-2" style="width: 20px; height: 20px;" />
				<span class="tittle_section">Evolución de Gastos & Eficiencia</span>
			</div>
			<div class="table_actions is-flex">
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Comparación
				</button>
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Ver Gráfico
				</button>
				<div style="min-width: 140px;">
					<SelectComponent :value="selectedPeriod" theme="report_theme"
						:options="periodOptions"
						@input="$emit('change-period', $event)" />
				</div>
			</div>
		</div>

		<section>
			<div v-if="isLoading" class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>

			<div v-else-if="error" class="error_state py-6 has-text-centered">
				<p class="has-text-danger">{{ error }}</p>
				<button class="action_btn mt-3" @click="$emit('retry')">Reintentar</button>
			</div>

			<div v-else-if="monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
				<p>No hay datos de gastos para el período seleccionado.</p>
			</div>

			<div v-else class="table_container" ref="mainTable">
				<table class="custom_table">
					<colgroup>
						<col style="width: 240px;" />
						<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
					</colgroup>
					<thead>
						<tr>
							<th scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in monthHeaders" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in visibleRows" :key="row.label"
							:class="['theme_row_white', { 'row-level-2': row.level === 2, 'row-level-3': row.level === 3 }]">
							<th scope="row"
								:class="['sticky-column nobreak theme_row_white is-capitalized', row.level === 3 ? 'pl-5' : '']"
								@click="row.level === 2 ? $emit('toggle-l2', row.label) : null"
								:style="row.level === 2 ? 'cursor: pointer;' : ''">
								<div class="is-flex is-align-items-center is-justify-content-space-between">
									<div class="is-flex is-align-items-center">
										<span v-if="row.level === 3" class="indent-symbol mr-1">↳</span>
										{{ row.label }}
									</div>
									<ArrowDown v-if="row.level === 2" class="expand-icon"
										:class="{ 'rotate-icon': expandedL2Rows.includes(row.label) }" />
								</div>
							</th>
							<td v-for="(val, idx) in row.values" :key="idx"
								class="has-text-right nobreak theme_row_white">
								{{ parseToMoney(val) }}
							</td>
						</tr>

						<tr class="theme_row_highlight">
							<th scope="row" class="sticky-column nobreak theme_row_highlight">
								{{ totalRow.label }}
							</th>
							<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
								class="has-text-right nobreak theme_row_highlight">
								{{ parseToMoney(val) }}
							</td>
						</tr>

						<tr class="theme_row_secondary">
							<th scope="row" class="sticky-column nobreak theme_row_secondary">
								% Gastos / Ventas
							</th>
							<td v-for="(_val, idx) in totalRow.values" :key="'eff-' + idx"
								class="has-text-right nobreak theme_row_secondary">
								12%
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import SelectComponent from "@/components/classification/SelectComponent";
import Ecs from "@/components/svg/Ecs.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import ChartLine from "@/components/svg/ChartLine.vue";

export default {
	name: "ExpensesEvolutionTable",

	components: { SelectComponent, Ecs, ArrowDown, ChartLine },

	props: {
		monthHeaders: { type: Array, required: true },
		visibleRows: { type: Array, required: true },
		totalRow: { type: Object, required: true },
		expandedL2Rows: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: null },
		selectedPeriod: { type: String, required: true },
		periodOptions: { type: Array, required: true },
	},

	methods: {
		parseToMoney,
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) table.scrollLeft = table.scrollWidth;
			});
		},
	},

	watch: {
		monthHeaders() { this.scrollToRight(); },
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: white;
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
}

.action_btn {
	background: white;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover { background: #f9fafb; }
}

.table_container {
	overflow-x: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	th, td {
		padding: 12px 16px;
		border-bottom: 1px solid #f3f4f6;
		font-size: 13px;
	}

	thead th {
		background: #f9fafb;
		color: #6b7280;
		font-weight: 700;
		text-align: right;
	}

	.sticky-column {
		position: sticky;
		left: 0;
		z-index: 2;
		border-right: 1px solid #e5e7eb;
	}

	.theme_row_white { background: white; }

	.theme_row_highlight {
		background: #fef3c7;
		font-weight: 700;

		th, td { border-bottom: 1px solid #fbbf24; }
	}

	.theme_row_secondary {
		background: #f9fafb;
		color: #6b7280;
		font-weight: 600;
	}

	.row-level-2 {
		font-weight: 700;
		background: #fcfcfc;
	}

	.row-level-3 {
		color: #4b5563;

		th, td {
			font-size: 12.5px !important;
			color: #6b7280 !important;
		}
	}

	.pl-5 { padding-left: 32px !important; }

	.indent-symbol {
		color: #9ca3af;
		font-weight: 400;
	}

	.expand-icon {
		width: 14px;
		height: 14px;
		transition: transform 0.3s ease;
		filter: brightness(0) saturate(100%) invert(60%) sepia(5%) saturate(100%) hue-rotate(180deg);
	}

	.rotate-icon { transform: rotate(180deg); }
}
</style>
